import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { History } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { ScrollArea } from '../../../../components/ui/scroll-area';

import {
  BotHistoryLogs,
  getBotHistory,
} from '../../../../services/onlineEventService';
import { getCustomLogger } from '../../../../lib/logger';

const logger = getCustomLogger('BotHistory');

function CalendarTime({
  eventTime,
  scheduleTime,
}: {
  eventTime: null | string;
  scheduleTime: string;
}) {
  const isoTime = (eventTime ?? scheduleTime)?.trim();
  const { t } = useTranslation();
  if (!isoTime) return null;
  const m = moment(isoTime, moment.ISO_8601, true); // Strict ISO parsing
  if (!m.isValid())
    return (
      <span>
        {t('settings.meetingBot.history.invalidDate', 'Invalid date')}
      </span>
    );
  return (
    <span>
      {m.calendar(null, {
        sameDay: 'h:mm A',
        lastDay: '[Yesterday]',
        lastWeek: 'ddd',
        sameElse: 'MMM D',
      })}
    </span>
  );
}

const BotHistory = () => {
  const { t } = useTranslation();

  const [logs, setLogs] = useState<BotHistoryLogs[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
        const results = await getBotHistory(timeZone);
        setLogs(results.logs);
      } catch (err) {
        logger.error(err);
      }
    };
    loadLogs();
  }, []);

  const handleViewClick = () => {
    setIsDialogOpen(true);
  };

  return (
    <>
      <Button
        variant="minimal"
        onClick={() => handleViewClick()}
        data-testid="view-bot-history-button"
        className="text-black hover:text-black"
      >
        {t('settings.meetingBot.history.viewHistory', 'View History')}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {t('settings.meetingBot.history.title', 'Meeting Bot History')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'settings.meetingBot.history.description',
                'Recent meeting bot activities and status updates',
              )}
            </DialogDescription>
          </DialogHeader>

          {logs.length > 0 ? (
            <ScrollArea className="h-[350px] pr-4">
              <div className="space-y-3">
                {logs.map(
                  ({ _id, title, message, eventTime, scheduleTime }) => (
                    <div
                      key={_id}
                      className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-none">
                            {title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {message}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          <CalendarTime
                            scheduleTime={scheduleTime}
                            eventTime={eventTime}
                          />
                        </span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <History className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-sm text-muted-foreground">
                {t(
                  'settings.meetingBot.history.noRecentHistory',
                  'No recent history found',
                )}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BotHistory;
